import { useState } from "react";
import { CalendarDays, MapPin, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import AnimateOnScroll from "../AnimateOnScroll";
import { useEvents } from "@/hooks/useEvents";
import {
  formatEventDate,
  formatEventTime,
  resolveEventImageUrl,
  type EventRow,
} from "@/lib/events";
import EventSignupModal from "./EventSignupModal";

interface EventsSectionProps {
  heading: string;
  /** Når satt vises denne meldingen istedenfor å skjule seksjonen om
   *  events-listen er tom. Brukes på /arrangementer; forsiden lar den stå
   *  udefinert så seksjonen forsvinner hvis det ikke er noe å vise. */
  emptyMessage?: string;
}

const buttonClass =
  "mt-5 inline-flex w-full items-center justify-center rounded-full bg-primary px-5 py-2.5 font-body text-sm font-semibold text-primary-foreground transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0";

interface SignupCtaProps {
  event: EventRow;
  onOpen: () => void;
}

const SignupCta = ({ event, onOpen }: SignupCtaProps) => {
  if (event.enable_signup) {
    const deadlinePassed =
      event.signup_deadline != null && new Date(event.signup_deadline).getTime() < Date.now();
    if (deadlinePassed) {
      return (
        <button type="button" disabled className={buttonClass}>
          Påmelding stengt
        </button>
      );
    }
    return (
      <button type="button" onClick={onOpen} className={buttonClass}>
        Meld deg på
      </button>
    );
  }
  if (event.signup_href) {
    return (
      <Link to={event.signup_href} className={buttonClass}>
        Meld deg på
      </Link>
    );
  }
  return null;
};

const EventsSection = ({ heading, emptyMessage }: EventsSectionProps) => {
  const { data: events, isLoading } = useEvents();
  const [activeEvent, setActiveEvent] = useState<EventRow | null>(null);

  if (isLoading) return null;
  const isEmpty = !events || events.length === 0;
  if (isEmpty && !emptyMessage) return null;

  if (isEmpty) {
    return (
      <section className="px-6 py-20 md:py-28">
        <div className="mx-auto max-w-3xl">
          <AnimateOnScroll>
            <h2 className="text-center font-heading text-3xl font-bold md:text-4xl">
              {heading}
            </h2>
          </AnimateOnScroll>
          <AnimateOnScroll delay="delay-200">
            <div className="mt-10 rounded-2xl border border-dashed border-primary/30 bg-card/60 px-8 py-12 text-center">
              <p className="font-body text-lg text-muted-foreground">{emptyMessage}</p>
            </div>
          </AnimateOnScroll>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-20 md:py-28">
      <div className="mx-auto max-w-6xl">
        <AnimateOnScroll>
          <h2 className="text-center font-heading text-3xl font-bold md:text-4xl">
            {heading}
          </h2>
        </AnimateOnScroll>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {events.map((event, i) => {
            const imageUrl = resolveEventImageUrl(event.image_path);
            return (
              <AnimateOnScroll
                key={event.id}
                animation="animate-fade-in-up"
                delay={`delay-${(i + 1) * 100}`}
              >
                <div className="group flex h-full flex-col overflow-hidden rounded-2xl bg-card shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1">
                  {imageUrl && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={imageUrl}
                        alt={event.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        width={600}
                        height={338}
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-heading text-lg font-bold">{event.title}</h3>
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground font-body">
                        <CalendarDays className="h-4 w-4 shrink-0 text-primary" />
                        <span>{formatEventDate(event.starts_at)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground font-body">
                        <Clock className="h-4 w-4 shrink-0 text-primary" />
                        <span>{formatEventTime(event.starts_at)}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground font-body">
                          <MapPin className="h-4 w-4 shrink-0 text-primary" />
                          <span>{event.location}</span>
                        </div>
                      )}
                    </div>
                    {event.body && (
                      <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground font-body">
                        {event.body}
                      </p>
                    )}
                    <SignupCta event={event} onOpen={() => setActiveEvent(event)} />
                  </div>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>

      {activeEvent && (
        <EventSignupModal
          event={activeEvent}
          open={Boolean(activeEvent)}
          onOpenChange={(open) => !open && setActiveEvent(null)}
        />
      )}
    </section>
  );
};

export default EventsSection;
